import { useT } from "../../../shared/i18n/i18n";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Placeholder } from "@tiptap/extensions";
import StarterKit from "@tiptap/starter-kit";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TableKit } from "@tiptap/extension-table";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import { DragHandle } from "@tiptap/extension-drag-handle-react";
import { TRPCClientError } from "@trpc/client";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowUp,
  Bold,
  Code,
  Copy,
  GripVertical,
  Highlighter,
  Italic,
  Link as LinkIcon,
  Plus,
  Repeat2,
  Strikethrough,
  Trash2,
  Underline,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import { exitSuggestion } from "@tiptap/suggestion";
import { createPortal } from "react-dom";
import { cn } from "../../../shared/lib/cn";
import { BlockColorSubmenu, SelectionColorButton } from "./color-menu";
import { DocumentCodeBlock, lowlight } from "./code-block-node";
import { EditorKeymap } from "./editor-keymap";
import { insertUploadedFiles, uploadStateEvent } from "./editor-uploads";
import { DocumentImage } from "./image-node";
import { LinkPopover } from "./link-popover";
import { filterSlashItems, readSlashState, SlashCommand, slashPluginKey, type SlashItem } from "./slash-command";
import { TableHandleExtension } from "./table/table-handle-extension";
import { TableHandles } from "./table/table-handles";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

type SaveState = "conflict" | "error" | "idle" | "saved" | "saving";

const saveDebounceMs = 1_200;

// Notion-like autosaving editor over a versioned document: "/" opens the
// block palette, selecting text raises a formatting bubble, every block gets
// a drag handle. Persist runs a compare-and-swap on the server; a lost race
// surfaces as a conflict banner instead of silently overwriting.
export function DocumentEditor({
  document: documentRecord,
  editable = true,
  editorClassName,
  onReload,
  persist,
  placeholder,
}: {
  document: { content: unknown; id: string; version: number };
  editable?: boolean;
  editorClassName?: string;
  onReload: () => Promise<void>;
  persist: (input: { content: Record<string, unknown>; documentId: string; version: number }) => Promise<{ version: number }>;
  placeholder?: string;
}) {
  const t = useT();
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const versionRef = useRef(documentRecord.version);
  const dirtyRef = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistRef = useRef(persist);
  persistRef.current = persist;

  const [slashIndex, setSlashIndex] = useState(0);
  const slashIndexRef = useRef(slashIndex);
  slashIndexRef.current = slashIndex;
  const [uploadingCount, setUploadingCount] = useState(0);

  const editor = useEditor(
    {
      content: (documentRecord.content as never) ?? "",
      editable,
      editorProps: {
        attributes: {
          class: cn("document-editor min-h-24 text-sm focus:outline-none", editorClassName),
        },
        // Dropped or pasted files become universal attachments; only their
        // capability paths enter the content (anything else fails the server
        // sanitizer and would reject the whole save).
        handleDrop: (view, event, _slice, moved) => {
          if (moved) return false;
          const files = [...(event.dataTransfer?.files ?? [])];
          if (files.length === 0) return false;
          event.preventDefault();
          const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
          const instance = editorRef.current;
          if (instance) void insertUploadedFiles(instance, files, coords?.pos ?? null);
          return true;
        },
        handlePaste: (_view, event) => {
          const files = [...(event.clipboardData?.files ?? [])];
          if (files.length === 0) return false;
          event.preventDefault();
          const instance = editorRef.current;
          if (instance) void insertUploadedFiles(instance, files, null);
          return true;
        },
      },
      extensions: [
        StarterKit.configure({
          codeBlock: false,
          heading: { levels: [1, 2, 3] },
          link: { autolink: true, openOnClick: false },
        }),
        DocumentCodeBlock.configure({ lowlight }),
        TaskList,
        TaskItem.configure({ nested: true }),
        TableKit.configure({ table: { allowTableNodeSelection: true, lastColumnResizable: false, resizable: true } }),
        TableHandleExtension,
        DocumentImage,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        Placeholder.configure({
          placeholder: ({ editor: instance, node }) => {
            if (node.type.name === "heading") return t("document.headingPlaceholder", { level: node.attrs.level });
            return instance.isEmpty ? placeholder ?? t("document.startWriting") : t("document.commandHint");
          },
        }),
        SlashCommand,
        EditorKeymap,
      ],
      onUpdate: () => {
        dirtyRef.current = true;
        setSaveState("saving");
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => void flush(), saveDebounceMs);
      },
    },
    [documentRecord.id],
  );
  const editorRef = useRef(editor);
  editorRef.current = editor;

  useEffect(() => {
    if (!import.meta.env.DEV || !editor) return;
    // Debug hook: __docEditor is only ever a MOUNTED editor (cleared on
    // unmount, never left dangling), __docEditorLog counts (re)creations.
    const w = window as { __docEditor?: unknown; __docEditorLog?: number[] };
    w.__docEditor = editor;
    (w.__docEditorLog = w.__docEditorLog ?? []).push(Date.now());
    return () => {
      if (w.__docEditor === editor) w.__docEditor = undefined;
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    const onUploadState = (event: Event) => setUploadingCount((event as CustomEvent<number>).detail ?? 0);
    dom.addEventListener(uploadStateEvent, onUploadState);
    return () => dom.removeEventListener(uploadStateEvent, onUploadState);
  }, [editor]);

  // Tiptap's Placeholder only decorates the node with the cursor, so an empty
  // unfocused editor renders as invisible blank space; this overlay keeps the
  // hint readable until the user clicks in. A doc is blank only when it is a
  // single empty textblock — editor.isEmpty also holds for text-less block
  // content (an empty table), which must not get the overlay painted over it.
  const emptyState = useEditorState({
    editor,
    selector: (context) => {
      const doc = context.editor?.state.doc;
      const firstChild = doc?.firstChild ?? null;
      return {
        isBlank: doc ? doc.childCount === 1 && firstChild !== null && firstChild.isTextblock && firstChild.content.size === 0 : false,
        isFocused: context.editor?.isFocused ?? false,
      };
    },
  });
  const showIdlePlaceholder = editable && emptyState !== null && emptyState.isBlank && !emptyState.isFocused;

  // The "/" palette renders straight from the suggestion plugin state — the
  // plugin's render hooks can be bound to a stale React instance (tiptap
  // defers editor destruction across strict-mode remounts), plugin state
  // cannot.
  const slashState = useEditorState({
    editor,
    selector: (context) => (context.editor ? readSlashState(context.editor) : null),
  });
  const slashMenuItems = slashState ? filterSlashItems(slashState.query, t) : [];

  const slashFloating = useFloating({
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    open: slashState !== null && slashMenuItems.length > 0,
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
  });
  const slashFloatingRef = useRef(slashFloating);
  slashFloatingRef.current = slashFloating;

  useEffect(() => {
    setSlashIndex(0);
  }, [slashState?.from, slashState?.query]);

  useEffect(() => {
    if (!editor || !slashState) return;
    const from = slashState.from;
    slashFloatingRef.current.refs.setReference({
      contextElement: editor.view.dom,
      getBoundingClientRect: () => {
        try {
          const coords = editor.view.coordsAtPos(from);
          return new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top);
        } catch {
          return new DOMRect(-9999, -9999, 0, 0);
        }
      },
    });
    slashFloatingRef.current.update();
  }, [editor, slashState]);

  // Arrow/Enter handling reaches the mounted component through
  // editor.storage — see SlashCommand for why.
  useEffect(() => {
    if (!editor) return;
    const storage = (editor.storage as unknown as Record<string, { onKeyDown: ((event: KeyboardEvent) => boolean) | null }>).slashCommand;
    if (!storage) return;
    storage.onKeyDown = (event) => {
      const state = readSlashState(editor);
      if (!state) return false;
      if (event.key === "Escape") {
        // Consume it fully: an Escape that closes the palette must not reach
        // the surrounding dialog's document listener and close the dialog too.
        event.preventDefault();
        event.stopPropagation();
        exitSuggestion(editor.view, slashPluginKey);
        return true;
      }
      const items = filterSlashItems(state.query, t);
      if (items.length === 0) return false;
      if (event.key === "ArrowDown") {
        setSlashIndex((index) => (index + 1) % items.length);
        return true;
      }
      if (event.key === "ArrowUp") {
        setSlashIndex((index) => (index - 1 + items.length) % items.length);
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        const item = items[Math.min(slashIndexRef.current, items.length - 1)];
        if (item) item.run(editor, { from: state.from, to: state.to });
        return true;
      }
      return false;
    };
    return () => {
      storage.onKeyDown = null;
    };
  }, [editor]);

  async function flush() {
    const instance = editorRef.current;
    if (!instance || !dirtyRef.current) return;
    dirtyRef.current = false;
    try {
      const updated = await persistRef.current({
        content: instance.getJSON() as Record<string, unknown>,
        documentId: documentRecord.id,
        version: versionRef.current,
      });
      versionRef.current = updated.version;
      if (!dirtyRef.current) setSaveState("saved");
    } catch (error) {
      dirtyRef.current = true;
      if (error instanceof TRPCClientError && error.data?.code === "CONFLICT") {
        setSaveState("conflict");
      } else {
        setSaveState("error");
      }
    }
  }

  // External updates (live invalidation, reload) land here: apply them only
  // while the local editor has nothing unsaved and the user is not typing —
  // setContent resets the cursor, which must never happen mid-edit. An
  // explicit conflict reload skips those guards: the user asked for it.
  const forceApplyRef = useRef(false);
  useEffect(() => {
    if (!editor || documentRecord.version <= versionRef.current) return;
    if (!forceApplyRef.current && (dirtyRef.current || editor.isFocused)) return;
    forceApplyRef.current = false;
    versionRef.current = documentRecord.version;
    editor.commands.setContent((documentRecord.content as never) ?? "");
    setSaveState("idle");
  }, [documentRecord.content, documentRecord.version, editor]);

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      // Flush pending edits when the editor unmounts (dialog close, tab switch).
      void flush();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  async function reloadAfterConflict() {
    dirtyRef.current = false;
    forceApplyRef.current = true;
    setSaveState("idle");
    await onReload();
  }

  return (
    <div className="group/document relative flex min-w-0 flex-col">
      {saveState === "conflict" ? (
        <div className="mb-2 flex items-center gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
          <p className="min-w-0 flex-1 text-xs">{t("document.staleReload")}</p>
          <Button onClick={() => void reloadAfterConflict()} size="sm" variant="outline">
            {t("editor.reload")}
          </Button>
        </div>
      ) : null}

      <span className="pointer-events-none absolute -top-5 right-0 text-[0.65rem] text-muted-foreground/70">
        {uploadingCount > 0
          ? t("document.uploading")
          : saveState === "saving"
            ? t("document.saving")
            : saveState === "saved"
              ? t("document.saved")
              : saveState === "error"
                ? t("document.saveFailed")
                : null}
      </span>

      {editor && editable ? <SelectionToolbar editor={editor} /> : null}
      {editor && editable ? <BlockDragHandle editor={editor} /> : null}
      {editor && editable ? <TableHandles editor={editor} /> : null}
      {editor && editable ? <LinkPopover editor={editor} /> : null}

      <div className="relative">
        {showIdlePlaceholder ? (
          <span aria-hidden className="pointer-events-none absolute left-0 top-0 text-sm text-muted-foreground/50">
            {placeholder}
          </span>
        ) : null}
        <EditorContent editor={editor} />
      </div>

      {editor && slashState && slashMenuItems.length > 0 && typeof document !== "undefined"
        ? createPortal(
            <div
              aria-label={t("editor.insertBlock")}
              className="z-50 w-64 overflow-hidden rounded-md border border-border/70 bg-popover p-1 shadow-md"
              ref={slashFloating.refs.setFloating}
              role="listbox"
              style={{ ...slashFloating.floatingStyles, maxHeight: "18rem", overflowY: "auto" }}
            >
              {slashMenuItems.map((item, index) => (
                <button
                  aria-selected={index === Math.min(slashIndex, slashMenuItems.length - 1)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left",
                    index === Math.min(slashIndex, slashMenuItems.length - 1) ? "bg-accent" : "hover:bg-accent/50",
                  )}
                  key={t(item.labelKey)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    item.run(editor, { from: slashState.from, to: slashState.to });
                  }}
                  onMouseEnter={() => setSlashIndex(index)}
                  role="option"
                  type="button"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/30 text-muted-foreground">
                    <item.icon className="size-4" />
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="text-sm">{t(item.labelKey)}</span>
                    <span className="text-xs text-muted-foreground/70">{t(item.subtextKey)}</span>
                  </span>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

const blockOptions: Array<{ isActive: (editor: Editor) => boolean; labelKey: string; run: (editor: Editor) => void }> = [
  {
    isActive: (editor) => editor.isActive("paragraph"),
    labelKey: "editor.text",
    run: (editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    isActive: (editor) => editor.isActive("heading", { level: 1 }),
    labelKey: "editor.heading1",
    run: (editor) => editor.chain().focus().setNode("heading", { level: 1 }).run(),
  },
  {
    isActive: (editor) => editor.isActive("heading", { level: 2 }),
    labelKey: "editor.heading2",
    run: (editor) => editor.chain().focus().setNode("heading", { level: 2 }).run(),
  },
  {
    isActive: (editor) => editor.isActive("heading", { level: 3 }),
    labelKey: "editor.heading3",
    run: (editor) => editor.chain().focus().setNode("heading", { level: 3 }).run(),
  },
  {
    isActive: (editor) => editor.isActive("bulletList"),
    labelKey: "editor.bulletList",
    run: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    isActive: (editor) => editor.isActive("orderedList"),
    labelKey: "editor.numberedList",
    run: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    isActive: (editor) => editor.isActive("taskList"),
    labelKey: "editor.todoList",
    run: (editor) => editor.chain().focus().toggleTaskList().run(),
  },
  {
    isActive: (editor) => editor.isActive("blockquote"),
    labelKey: "editor.quote",
    run: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
  {
    isActive: (editor) => editor.isActive("codeBlock"),
    labelKey: "editor.codeBlock",
    run: (editor) => editor.chain().focus().toggleCodeBlock().run(),
  },
];

// Formatting bubble over the current selection: marks, link editing and a
// turn-into dropdown for the containing block.
function SelectionToolbar({ editor }: { editor: Editor }) {
  const t = useT();
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");

  const state = useEditorState({
    editor,
    selector: (context) => ({
      isBold: context.editor?.isActive("bold") ?? false,
      isCode: context.editor?.isActive("code") ?? false,
      isHighlight: context.editor?.isActive("highlight") ?? false,
      isItalic: context.editor?.isActive("italic") ?? false,
      isLink: context.editor?.isActive("link") ?? false,
      isStrike: context.editor?.isActive("strike") ?? false,
      isUnderline: context.editor?.isActive("underline") ?? false,
    }),
  });
  const activeBlock = blockOptions.find((option) => option.isActive(editor));

  function applyLink() {
    const href = linkValue.trim();
    if (href.length === 0) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      const normalized = /^(https?:|mailto:)/i.test(href) ? href : `https://${href}`;
      editor.chain().focus().extendMarkRange("link").setLink({ href: normalized }).run();
    }
    setIsLinkOpen(false);
    setLinkValue("");
  }

  return (
    <BubbleMenu
      className="flex items-center gap-0.5 rounded-md border border-border/70 bg-popover p-1 shadow-md"
      editor={editor}
      options={{ offset: 6, placement: "top" }}
      shouldShow={({ editor: instance, state: editorState }) => {
        if (!instance.isEditable || editorState.selection.empty) return false;
        // Only text ranges get the formatting bubble — node selections (a
        // dropped block) and table cell selections have their own controls.
        if (!(editorState.selection instanceof TextSelection)) return false;
        return !instance.isActive("codeBlock");
      }}
      updateDelay={150}
    >
      {isLinkOpen ? (
        <div className="flex items-center gap-1 px-0.5" data-editor-chrome>
          <Input
            autoFocus
            className="h-7 w-56 text-xs"
            onChange={(event) => setLinkValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applyLink();
              }
              if (event.key === "Escape") {
                // Cancel only the link editor, not the surrounding dialog.
                event.preventDefault();
                event.stopPropagation();
                setIsLinkOpen(false);
              }
            }}
            placeholder={t("editor.linkHint")}
            value={linkValue}
          />
          <Button onClick={applyLink} size="sm" variant="secondary">
            {t("editor.set")}
          </Button>
        </div>
      ) : (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button className="h-7 gap-1 px-2 text-xs" size="sm" variant="ghost">
                  {activeBlock ? t(activeBlock.labelKey) : t("editor.text")}
                </Button>
              }
            />
            <DropdownMenuContent align="start" className="w-40">
              {blockOptions.map((option) => (
                <DropdownMenuItem
                  className={cn(option.isActive(editor) && "bg-accent/60")}
                  key={option.labelKey}
                  onClick={() => option.run(editor)}
                >
                  {t(option.labelKey)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="mx-0.5 h-4 w-px bg-border/70" />
          <MarkButton active={state?.isBold} label={t("editor.bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
            <Bold />
          </MarkButton>
          <MarkButton active={state?.isItalic} label={t("editor.italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Italic />
          </MarkButton>
          <MarkButton active={state?.isUnderline} label={t("editor.underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <Underline />
          </MarkButton>
          <MarkButton active={state?.isStrike} label={t("editor.strikethrough")} onClick={() => editor.chain().focus().toggleStrike().run()}>
            <Strikethrough />
          </MarkButton>
          <MarkButton active={state?.isCode} label={t("editor.code")} onClick={() => editor.chain().focus().toggleCode().run()}>
            <Code />
          </MarkButton>
          <MarkButton active={state?.isHighlight} label={t("editor.highlight")} onClick={() => editor.chain().focus().toggleHighlight().run()}>
            <Highlighter />
          </MarkButton>
          <SelectionColorButton editor={editor} />
          <span className="mx-0.5 h-4 w-px bg-border/70" />
          <MarkButton
            active={editor.isActive({ textAlign: "left" })}
            label={t("editor.alignLeft")}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <AlignLeft />
          </MarkButton>
          <MarkButton
            active={editor.isActive({ textAlign: "center" })}
            label={t("editor.alignCenter")}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <AlignCenter />
          </MarkButton>
          <MarkButton
            active={editor.isActive({ textAlign: "right" })}
            label={t("editor.alignRight")}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <AlignRight />
          </MarkButton>
          <MarkButton
            active={state?.isLink}
            label={t("editor.link")}
            onClick={() => {
              setLinkValue((editor.getAttributes("link").href as string | undefined) ?? "");
              setIsLinkOpen(true);
            }}
          >
            <LinkIcon />
          </MarkButton>
        </>
      )}
    </BubbleMenu>
  );
}

function blockDisplayName(node: ProseMirrorNode): string {
  switch (node.type.name) {
    case "blockquote":
      return "Quote";
    case "bulletList":
      return "Bullet list";
    case "codeBlock":
      return "Code block";
    case "heading":
      return `Heading ${node.attrs.level}`;
    case "horizontalRule":
      return "Divider";
    case "image":
      return "Image";
    case "orderedList":
      return "Numbered list";
    case "paragraph":
      return "Text";
    case "table":
      return "Table";
    case "taskList":
      return "To-do list";
    default:
      return "Block";
  }
}

// Block types the "Turn into" transforms make sense for — a table or divider
// must not get its inner paragraph converted.
const turnIntoBlockTypes = new Set(["paragraph", "heading", "bulletList", "orderedList", "taskList", "blockquote", "codeBlock"]);

function blockElementAt(editor: Editor, pos: number): HTMLElement | null {
  const dom = editor.view.nodeDOM(pos);
  return dom instanceof HTMLElement ? dom : null;
}

// The floating block handle, mnemos-style: plus inserts a block below and
// opens the "/" palette; the grip drags to reorder, and a click on it opens
// the block options menu (turn into / move / duplicate / delete).
function BlockDragHandle({ editor }: { editor: Editor }) {
  const t = useT();
  const hoveredRef = useRef<{ pos: number; size: number } | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuNode, setMenuNode] = useState<ProseMirrorNode | null>(null);
  const editorRef = useRef(editor);
  editorRef.current = editor;

  // While the menu is open the handle must not chase the pointer or hide.
  useEffect(() => {
    editor.view.dispatch(editor.state.tr.setMeta("lockDragHandle", isMenuOpen));
    if (!isMenuOpen) editor.view.dispatch(editor.state.tr.setMeta("hideDragHandle", true));
  }, [editor, isMenuOpen]);

  // While the user is selecting text the handle gets out of the way — the
  // formatting bubble owns that moment (mnemos behaviour).
  const hasTextSelection = useEditorState({
    editor,
    selector: (context) => {
      const selection = context.editor?.state.selection;
      return selection instanceof TextSelection && !selection.empty;
    },
  });

  // Align the handle to the first text line of tall blocks (tables, lists)
  // instead of their top edge — ported from the mnemos drag-context-menu.
  const computePositionConfig = useMemo(
    () => ({
      middleware: [
        offset(({ rects }: { rects: { floating: { height: number } } }) => {
          const hovered = hoveredRef.current;
          const element = hovered ? blockElementAt(editorRef.current, hovered.pos) : null;
          let crossAxis = 0;
          if (element) {
            const style = window.getComputedStyle(element);
            const lineHeight = Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) || rects.floating.height;
            const paddingTop = Number.parseFloat(style.paddingTop) || 0;
            crossAxis = paddingTop + (lineHeight - rects.floating.height) / 2;
          }
          return { crossAxis, mainAxis: 8 };
        }),
      ],
      placement: "left-start" as const,
      strategy: "absolute" as const,
    }),
    [],
  );

  function menuTarget(): { node: ProseMirrorNode; pos: number } | null {
    const selection = editor.state.selection;
    if (selection instanceof NodeSelection) return { node: selection.node, pos: selection.from };
    const hovered = hoveredRef.current;
    if (!hovered) return null;
    const node = editor.state.doc.nodeAt(hovered.pos);
    return node ? { node, pos: hovered.pos } : null;
  }

  function runOnTarget(action: (target: { node: ProseMirrorNode; pos: number }) => void) {
    const target = menuTarget();
    if (target) action(target);
  }

  // Swap the block with its top-level sibling.
  function moveBlock(direction: -1 | 1) {
    runOnTarget(({ node, pos }) => {
      const doc = editor.state.doc;
      const $pos = doc.resolve(pos);
      if ($pos.depth !== 0) return;
      const index = $pos.index(0);
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= doc.childCount) return;
      const neighbor = doc.child(targetIndex);
      editor
        .chain()
        .command(({ tr }) => {
          tr.delete(pos, pos + node.nodeSize);
          const insertAt = direction === -1 ? pos - neighbor.nodeSize : pos + neighbor.nodeSize;
          tr.insert(insertAt, node);
          tr.setSelection(NodeSelection.create(tr.doc, insertAt));
          return true;
        })
        .focus()
        .run();
    });
  }

  const target = isMenuOpen ? menuTarget() : null;
  const targetIndex = target ? editor.state.doc.resolve(target.pos).index(0) : -1;
  const canTurnInto = menuNode !== null && turnIntoBlockTypes.has(menuNode.type.name);
  const canMoveUp = target !== null && targetIndex > 0;
  const canMoveDown = target !== null && targetIndex >= 0 && targetIndex < editor.state.doc.childCount - 1;

  return (
    <DragHandle
      className="document-drag-handle"
      computePositionConfig={computePositionConfig}
      editor={editor}
      // Re-focusing after a drop clears the browser's lingering drag state
      // (mnemos does the same).
      onElementDragEnd={() => {
        setTimeout(() => {
          editor.view.dom.blur();
          editor.view.focus();
        }, 0);
      }}
      onNodeChange={({ node, pos }) => {
        hoveredRef.current = node && pos >= 0 ? { pos, size: node.nodeSize } : null;
      }}
    >
      <div className="flex" style={hasTextSelection && !isMenuOpen ? { opacity: 0, pointerEvents: "none" } : undefined}>
      <button
        aria-label={t("editor.insertBelow")}
        className="document-drag-handle__button"
        onMouseDown={(event) => {
          event.preventDefault();
          const hovered = hoveredRef.current;
          const insertAt = hovered ? hovered.pos + hovered.size : editor.state.selection.to;
          editor
            .chain()
            .insertContentAt(insertAt, { type: "paragraph" })
            .focus(insertAt + 1)
            .insertContent("/")
            .run();
        }}
        type="button"
      >
        <Plus className="size-3.5" />
      </button>
      <DropdownMenu onOpenChange={setIsMenuOpen} open={isMenuOpen}>
        <DropdownMenuTrigger
          render={
            <button
              aria-label={t("editor.blockOptions")}
              className="document-drag-handle__button"
              data-drag
              // A drag that starts from the grip must not leave the menu open.
              onDragStart={() => setIsMenuOpen(false)}
              onPointerDown={() => {
                // Select the hovered block so the menu visually targets it.
                const hovered = hoveredRef.current;
                if (!hovered) return;
                editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, hovered.pos)));
                setMenuNode(editor.state.doc.nodeAt(hovered.pos));
              }}
              type="button"
            >
              <GripVertical className="size-3.5" />
            </button>
          }
        />
        <DropdownMenuContent align="start" className="w-52" side="left">
          <DropdownMenuGroup>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {menuNode ? blockDisplayName(menuNode) : "Block"}
            </DropdownMenuLabel>
          </DropdownMenuGroup>
          {canTurnInto ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Repeat2 data-icon="inline-start" />
                {t("editor.turnInto")}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-44">
                {blockOptions.map((option) => (
                  <DropdownMenuItem
                    className={cn(option.isActive(editor) && "bg-accent/60")}
                    key={option.labelKey}
                    onClick={() =>
                      runOnTarget(({ pos }) => {
                        // Transforms operate on a caret inside the block, not
                        // on the node selection.
                        editor.chain().setTextSelection(pos + 1).run();
                        option.run(editor);
                      })
                    }
                  >
                    {t(option.labelKey)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : null}
          {canTurnInto ? (
            <BlockColorSubmenu
              editor={editor}
              getTarget={() => {
                const target = menuTarget();
                return target ? { pos: target.pos, size: target.node.nodeSize } : null;
              }}
            />
          ) : null}
          <DropdownMenuItem disabled={!canMoveUp} onClick={() => moveBlock(-1)}>
            <ArrowUp data-icon="inline-start" />
            {t("editor.moveUp")}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!canMoveDown} onClick={() => moveBlock(1)}>
            <ArrowDown data-icon="inline-start" />
            {t("editor.moveDown")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              runOnTarget(({ node, pos }) => {
                editor.chain().focus().insertContentAt(pos + node.nodeSize, node.toJSON() as never).run();
              })
            }
          >
            <Copy data-icon="inline-start" />
            {t("editor.duplicate")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              runOnTarget(({ node, pos }) => {
                editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
              })
            }
            variant="destructive"
          >
            <Trash2 data-icon="inline-start" />
            {t("editor.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </DragHandle>
  );
}

function MarkButton({
  active,
  children,
  label,
  onClick,
}: {
  active?: boolean;
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      aria-label={label}
      aria-pressed={active ?? false}
      className={cn("size-7 text-muted-foreground [&_svg]:size-3.5", active && "bg-accent text-foreground")}
      onClick={onClick}
      // Keep the selection: a toolbar click must not blur the editor.
      onMouseDown={(event) => event.preventDefault()}
      size="icon-sm"
      title={label}
      type="button"
      variant="ghost"
    >
      {children}
    </Button>
  );
}
